import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { warmMic, getMic, releaseMic } from "../lib/mic";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

/**
 * Voice-first big button: record speech -> Whisper transcript -> AI structured draft
 * (kind: resolution | task | material). Calls onDraft(draft) with the parsed fields.
 * Designed for installers mid-work: one big tap, speak, done.
 */
export function VoiceDraftButton({ kind, label, participants, onDraft, className = "" }) {
  const { lang, t } = useApp();
  const [state, setState] = useState("idle"); // idle | recording | processing
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  // Pre-warm the mic while the dialog is open so recording starts INSTANTLY on tap
  useEffect(() => {
    warmMic().catch(() => {});
    return () => releaseMic(1500);
  }, []);

  const start = async () => {
    try {
      const stream = await getMic();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setState("processing");
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "draft.webm");
          const res = await api.post("/transcribe", fd); // authenticated (AI consent gate)
          const text = (res.data.transcript || "").trim();
          if (!text) {
            toast.info(t("no_speech_detected"));
            setState("idle");
            return;
          }
          const draft = await api.post("/ai/field-draft", {
            text,
            language: lang,
            kind,
            participants: (participants || []).map((p) => ({ id: p.id, name: p.name })),
          });
          onDraft(draft.data);
        } catch (e) {
          toast.error("Failed");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      recRef.current = mr;
      setState("recording");
    } catch (e) {
      toast.error(t("mic_denied"));
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
  };

  if (state === "recording") {
    return (
      <button
        type="button"
        data-testid={`voice-draft-stop-${kind}`}
        onClick={stop}
        className={`w-full h-14 rounded-xl bg-destructive text-white flex items-center justify-center gap-3 font-semibold animate-pulse ${className}`}
      >
        <Square className="h-5 w-5" /> {t("listening_tap_stop")}
      </button>
    );
  }
  if (state === "processing") {
    return (
      <div
        data-testid={`voice-draft-processing-${kind}`}
        className={`w-full h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center gap-3 font-semibold ${className}`}
      >
        <Loader2 className="h-5 w-5 animate-spin" /> {t("processing_voice")}
      </div>
    );
  }
  return (
    <button
      type="button"
      data-testid={`voice-draft-start-${kind}`}
      onClick={start}
      className={`w-full h-14 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-3 font-semibold transition-colors ${className}`}
    >
      <Mic className="h-6 w-6" /> {label}
    </button>
  );
}
